import { checkPathTo } from '@adactiveasia/adsign-map';
import moment from 'moment';

/* eslint-disable global-require */
export const wait = (timeout) =>
    new Promise((resolve) => setTimeout(resolve, timeout));

export const toDataURL = (url, callback) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () {
            callback(reader.result);
        };
        reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
};

export const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch (_) {
        return false;
    }
};

export const parseMenuDescription = (description) => {
    if (!description) return null;

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(description, 'text/html');

        const liElements = Array.from(doc.querySelectorAll('li'));
        if (!liElements.length) return null;

        const result = {};

        liElements.forEach((li) => {
            const labelNode = li.firstChild;
            if (!labelNode) return;

            const labelText = labelNode.textContent.split(':')[0].trim();

            const valueHTML = li.innerHTML
                .replace(new RegExp(`^\\s*${labelText}:\\s*`, 'i'), '')
                .trim();

            if (labelText && valueHTML) {
                result[labelText] = valueHTML;
            }
        });

        return Object.keys(result).length ? result : null;
    } catch (error) {
        // console.error('Failed to parse menu description:', error);
        return null;
    }
};

export const filterAndSortMedias = (medias) => {
    return medias
        .filter((media) => {
            if (media.start && media.end) {
                return moment(new Date()).isBetween(
                    moment(media.start).startOf('minute').toISOString(),
                    moment(media.end).startOf('minute').toISOString()
                );
            } else {
                return true;
            }
        })
        .sort((a, b) => {
            const startA = a.eventStartDate ? moment(a.eventStartDate) : null;
            const startB = b.eventStartDate ? moment(b.eventStartDate) : null;
            const endA = a.eventEndDate ? moment(a.eventEndDate) : null;
            const endB = b.eventEndDate ? moment(b.eventEndDate) : null;

            if (startA && startB) {
                const startDiff = startA.diff(startB);
                if (startDiff !== 0) return startDiff;
            } else if (startA && !startB) {
                return -1;
            } else if (!startA && startB) {
                return 1;
            }

            if (endA && endB) {
                const endDiff = endA.diff(endB);
                if (endDiff !== 0) return endDiff;
            } else if (endA && !endB) {
                return -1;
            } else if (!endA && endB) {
                return 1;
            }

            const nameA = a.name?.toLowerCase() || '';
            const nameB = b.name?.toLowerCase() || '';
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;

            return 0;
        });
};

export const getLocationPoi = (mapStore, poi) => {
    const shapeTo = mapStore.freeShapes.find((data) => {
        if (poi.locations.length > 0) {
            return (
                poi.locations?.some((loc) => loc.id === data._id) ||
                poi.locations?.some((loc) => loc._id === data._id)
            );
        }
        return poi.location === data.name || poi.location === '#' + data.name;
    });
    if (!shapeTo) return ['Scape'];
    return [shapeTo?.name] || ['Scape'];
};

export const getCategoryById = (listCategory, categoryId) => {
    if (!listCategory) return;
    return listCategory.find((cat) => cat._id === categoryId);
};

export const getShapeByPoi = (poi, shapes) => {
    const shapeTo = shapes.find((data) => {
        if (poi.locations?.length > 0) {
            return (
                poi.locations.some((loc) => loc.id === data._id) ||
                poi.locations.some((loc) => loc._id === data._id)
            );
        }
        return poi.location === data.name || poi.location === '#' + data.name;
    });

    return shapeTo || null;
};

export const getPoiByShape = (shape, pois) => {
    const thePoi = pois.find((poi) => {
        if (poi.locations?.length > 0) {
            return (
                poi.locations.some((loc) => loc.id === shape._id) ||
                poi.locations.some((loc) => loc._id === shape._id)
            );
        }
        return poi.location === shape.name || poi.location === '#' + shape.name;
    });

    return thePoi || null;
};

export const convertDescription = (str) => {
    let json;
    try {
        json = JSON.parse(`${str}`);
        return json.description.replaceAll('\u00a0', ' ');
    } catch (error) {
        return str;
    }
};

export const getNearestShape = async (mapStore, shapes, type) => {
    if (!Array.isArray(shapes) || shapes.length === 0) return null;

    const getDistance = (nodes = []) => {
        let distance = 0;
        nodes.forEach((node, index) => {
            if (index < nodes.length - 1) {
                distance += node.position.distanceTo(nodes[index + 1].position);
            }
        });
        return distance;
    };

    const shapesWithDistance = await Promise.all(
        shapes.map(async (shape) => {
            const wayfinding =
                (await checkPathTo(mapStore, shape._id, type)) || [];

            const distance = getDistance(wayfinding);
            const floorsCount = [
                ...new Set(
                    (wayfinding || [])
                        .filter(
                            (node) =>
                                node.meshType === 'floor' ||
                                node.meshType === 'sub-floor'
                        )
                        .map((node) => node.meshId)
                ),
            ].length;

            return { ...shape, distance, floorsCount };
        })
    );

    const nearest = shapesWithDistance
        .filter((shape) => shape.distance > 0)
        .sort((a, b) => a.distance - b.distance)[0];

    if (!nearest) return null;

    const { distance, floorsCount, ...shapeOnly } = nearest;
    return shapeOnly;
};

export const checkPathToo = (mapStore, shapeId, type = "standard") => {
    if (!mapStore.aStar) {
      return null;
    }
    const shape = mapStore.freeShapes.find((_shape) => _shape._id == shapeId);
    const model3d = mapStore.model3ds.find((_model) => _model._id == shapeId);
    if (shape || model3d) {
      const startNode = mapStore.aStar.nodes.find(
        (node) => node.position.equals(
          mapStore.userLocation?.position ?? new import_three6.Vector3(0, 0, 0)
        ) && node.accessibility == "standard"
      );
     
      let endNode;
      if (shape) {
        endNode = mapStore.aStar.nodes.find(
          (node) => node.meshId == shape?._id && (node.accessibility == type || node.accessibility == "standard")
        );
      }
      if (model3d) {
        endNode = mapStore.aStar.nodes.find(
          (node) => node.meshId == model3d?._id && (node.accessibility == type || node.accessibility == "standard")
        );
      }

 
      if (!startNode || !endNode) return null;
      const rawPath = mapStore.aStar.searchPath(startNode, endNode, type);

        // eslint-disable-next-line no-console
        console.log("startNode", startNode, endNode, rawPath,type,  mapStore.aStar)
      return rawPath;
    } else {
      return null;
    }
  };